/**
 * Списки действий у оборудования: «Выполняемые операции», «Перед началом работы», «В течение смены»,
 * «По окончании смены» и свои этапы. Каждое действие — отдельный пункт.
 * Этапы-проверки (kind = 'check') оператор проходит с галочками: «в порядке» или «не в порядке» + комментарий и фото.
 * PDF-карточка — печатная версия того же; в приложении — электронная.
 */
import { z } from 'zod';

export type ProcedureKind = 'info' | 'check';

export interface ProcedureItem {
  id: string;
  text: string;
  /** Уточнение «как это сделать» — открывается по нажатию на пункт. */
  details?: string | null;
  /** Фото, видео, PDF к пункту (файлы этой единицы с пометкой «к пункту» — в общей галерее не показываются). */
  fileIds?: string[];
  /** Ссылка на типовое действие из библиотеки: текст, уточнение и файлы берутся оттуда. */
  actionId?: string | null;
}

export interface Procedure {
  /** Стандартные этапы: ops, before, during, after; свои — c_… */
  id: string;
  title: string;
  /** info — просто список (что делает машина); check — проверка с галочками. */
  kind: ProcedureKind;
  items: ProcedureItem[];
}

export const STANDARD_PROCEDURES: readonly { id: string; title: string; kind: ProcedureKind }[] = [
  { id: 'ops', title: 'Выполняемые операции', kind: 'info' },
  { id: 'before', title: 'Перед началом работы', kind: 'check' },
  { id: 'during', title: 'В течение рабочей смены', kind: 'check' },
  { id: 'after', title: 'По окончании рабочей смены', kind: 'check' },
];

const Id = z.string().regex(/^[A-Za-z0-9_-]{1,40}$/);

export const ProcedureInput = z.object({
  id: Id,
  title: z.string().trim().min(1, 'У этапа должно быть название').max(80),
  kind: z.enum(['info', 'check']),
  items: z
    .array(
      z.object({
        id: Id,
        text: z.string().trim().max(500),
        details: z.string().trim().max(3000).nullable().optional(),
        fileIds: z.array(z.string().uuid()).max(12).optional(),
        actionId: z.string().uuid().nullable().optional(),
      }),
    )
    .max(100)
    .transform((list) => list.filter((i) => i.text.length > 0)),
});
export const ProceduresInput = z.array(ProcedureInput).max(20);
/** Пункты без этапа (переналадка у номенклатуры). */
export const StepItemsInput = ProcedureInput.shape.items;

/** Короткий id для нового пункта или этапа. */
export function newId(prefix = ''): string {
  return prefix + Math.random().toString(36).slice(2, 10);
}

/** Стандартные этапы всегда на месте (пустые — тоже, чтобы было куда добавить), свои — после них. */
export function withStandard(list: Procedure[]): Procedure[] {
  const byId = new Map(list.map((p) => [p.id, p]));
  const std = STANDARD_PROCEDURES.map((s) => byId.get(s.id) ?? { ...s, items: [] });
  return [...std, ...list.filter((p) => !STANDARD_PROCEDURES.some((s) => s.id === p.id))];
}

const HEADS: [RegExp, string][] = [
  [/^выполняем[а-яё]*\s+операци/i, 'ops'],
  [/^(перед\s+начал[а-яё]*|до\s+начал[а-яё]*)/i, 'before'],
  [/^в\s+течени[а-яё]*/i, 'during'],
  [/^(по\s+окончани[а-яё]*|после\s+окончани[а-яё]*|в\s+конце)/i, 'after'],
];

/**
 * Из текста карточки (описание + инструкция из PDF) — списки по этапам и ответственный.
 *   «Перед началом работы:\n1. Проверить…\n2. …» → этап before с пунктами.
 * Строка без номера продолжает предыдущий пункт (перенос строки в PDF).
 */
export function proceduresFromText(...texts: (string | null | undefined)[]): { procedures: Procedure[]; responsible: string | null } {
  const out: Procedure[] = [];
  let responsible: string | null = null;
  let cur: Procedure | null = null;
  let n = 0;
  for (const raw of texts.filter(Boolean).join('\n').split(/\r?\n/)) {
    const line = raw.replace(/\s+/g, ' ').trim();
    if (!line) continue;
    const resp = /^ответственн[а-яё]*[^:]*:\s*(.+)$/i.exec(line);
    if (resp) {
      responsible = resp[1]!.trim().slice(0, 120);
      continue;
    }
    const item = /^(\d{1,2}[.)]|[-•–])\s*(.+)$/.exec(line);
    if (!item && /:$/.test(line) && line.length <= 80) {
      const title = line.slice(0, -1).trim();
      const std = HEADS.find(([re]) => re.test(title));
      const id = std ? std[1] : `c_${++n}`;
      cur = out.find((p) => p.id === id) ?? null;
      if (!cur) {
        const s = STANDARD_PROCEDURES.find((x) => x.id === id);
        cur = { id, title: s?.title ?? title, kind: s?.kind ?? 'check', items: [] };
        out.push(cur);
      }
      continue;
    }
    if (!cur) {
      cur = { id: `c_${++n}`, title: 'Порядок действий', kind: 'info', items: [] };
      out.push(cur);
    }
    if (item || cur.items.length === 0) cur.items.push({ id: `i${cur.id}_${cur.items.length + 1}`, text: (item ? item[2]! : line).slice(0, 500) });
    else {
      const last = cur.items[cur.items.length - 1]!;
      last.text = `${last.text} ${line}`.slice(0, 500);
    }
  }
  return { procedures: out.filter((p) => p.items.length > 0), responsible };
}

/* ---------- Проверки (прохождение этапа оператором) ---------- */

export type CheckState = 'ok' | 'bad';

export interface CheckItemDto {
  id: string;
  text: string;
  state: CheckState | null;
  note: string | null;
  /** Уточнение и файлы пункта — на момент начала проверки. */
  details?: string | null;
  fileIds?: string[];
  /** Файлы пункта (фото, видео, PDF «как сделать») — открываются через API с авторизацией. */
  files?: { id: string; step: boolean; kind: 'photo' | 'video' | 'doc'; title: string; mime: string; size: number; createdAt: string; url: string }[];
  /** Фото к замечанию — открываются через API (с авторизацией). */
  photoIds: string[];
}

export interface CheckDto {
  id: string;
  equipmentId: string;
  equipmentName: string;
  equipmentCode: string;
  procedureId: string;
  title: string;
  employeeId: string | null;
  employeeName: string;
  startedAt: string;
  finishedAt: string | null;
  /** open — идёт; ok — всё в порядке; issues — есть замечания. */
  status: 'open' | 'ok' | 'issues';
  items: CheckItemDto[];
}

export const CheckItemPatch = z.object({
  state: z.enum(['ok', 'bad']).nullable(),
  note: z.string().trim().max(1000).nullable().optional(),
});

export const CheckStart = z.object({ procedureId: Id });

const SHORT: Record<string, string> = { before: 'До смены', during: 'В смену', after: 'После смены' };
/** Короткое название этапа для кнопок и сводок. */
export function procedureShort(p: { id: string; title: string }): string {
  return SHORT[p.id] ?? p.title;
}
